<template>
  <div class="app-root">
    <header>
      <h2>Sõja tulipunktid jaanuaris</h2>
    </header>

    <main class="map"></main>
  </div>
</template>

<style scoped>
.app-root {
  --section-color: #0064fa;
  --section-secondary-color: #0000ba;
  --section-tertiary-color: #a5e6e9;
  --line: #d5d8dd;
  --gray: #717c8e;
  --lightest: #eef3f6;
  --text-color: #262626;
  --background-color: white;

  --map-width: v-bind(containerWidth + "px");

  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;

  font-family: GT Eesti, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;

  font-size: 16px;
  line-height: 1;

  color: var(--text-color);
  background-color: var(--background-color);

  -webkit-tap-highlight-color: transparent;
}

.dark-theme .app-root {
  --text-color: #eff3f5;
  --background-color: #141414;
  --lightest: #373c45;
}

header {
  margin-bottom: 0.75em;
}

h2 {
  margin: 0;
  line-height: 1.1;
  font-size: 2em;
}

.map {
  width: 100%;
  flex-grow: 1;

  box-sizing: border-box;
  border: 2px solid var(--lightest);

  position: relative;
}

@supports (mix-blend-mode: color-burn) {
  .map::after {
    content: "";

    pointer-events: none;

    position: absolute;
    z-index: 999;

    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    background-color: var(--gray);
    opacity: 0.15;
    mix-blend-mode: color-burn;
  }

  .dark-theme .map::after {
    opacity: 0.35;
  }
}
</style>

<script setup>
import { ref, computed, onMounted, onUnmounted, inject, nextTick } from "vue";

//
import ukrainaBaseMap from "./data/ukraina-basemap.json";
import redAreas from "./data/krimm-donbass.json";
import rindejoon from "./data/rindejoon_0131.json";

//
const containerElement = inject("containerElement");
const containerWidth = ref(0);

//
const markersData = [
  {
    name: "Svatove",
    coords: [49.409167, 38.161944],
    radius: 4000,
    tooltipPos: "left",
  },
  {
    name: "Kreminnia",
    coords: [49.05, 38.2167],
    radius: 4000,
    tooltipPos: "right",
  },
  {
    name: "Bahmut-Soledar",
    coords: [48.63205144895333, 38.022308349609375],
    radius: 12000,
    tooltipPos: "left",
  },
  {
    name: "Vuhledar",
    coords: [47.779167, 37.248333],
    radius: 1500,
    tooltipPos: "right",
  },
  {
    name: "Orikhiv",
    coords: [47.569444, 35.777778],
    radius: 1500,
    tooltipPos: "left",
  },
];

//
let map;

/**
 *
 */
async function initMap() {
  map = L.map(containerElement.querySelector(".map"), {
    onMapReady(map) {
      setTimeout(() => {
        map.invalidateSize(true);
      }, 1000);
    },
    zoomControl: false, // add manually
    minZoom: 4,
    zoom: 6,
    center: [48.5, 37],
  });

  const tileServer = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  const attribution = `&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> |
    <a href="https://postimees.ee">Postimees</a>`;

  const tiles = L.tileLayer(tileServer, { attribution });
  tiles.addTo(map);

  L.control
    .zoom({
      position: "topleft",
    })
    .addTo(map);

  L.control.scale({ imperial: false }).addTo(map);

  // kontuur
  const ukrainaOnMap = L.geoJSON(ukrainaBaseMap, {
    interactive: false,
    color: "rgb(0, 91, 187)",
    weight: 2,
    fillColor: "rgb(255, 213, 0)",
    fillOpacity: 0.01,
  }).addTo(map);

  // rindejoon
  const redAreaOptions = {
    color: "#cc0000",
    weight: 0,
    fillOpacity: 0.1,
  };
  const redAreasOnMap = L.geoJSON(redAreas, redAreaOptions)
    .bindPopup("Okupeeritud alad")
    .addTo(map);
  const rindejoonOnMap = L.geoJSON(rindejoon, redAreaOptions)
    .bindPopup("Rindejoon")
    .addTo(map);

  // markers
  const markerOptions = {
    color: "#cc0000",
  };
  markersData.forEach((item) => {
    item.m = L.circle(item.coords, {
      ...markerOptions,
      radius: item.radius,
    })
      .addTo(map)
      .bindTooltip(`<b>${item.name}</b>`, {
        direction: item.tooltipPos,
      });
  });
  const markers = markersData.map((item) => item.m);

  //
  await nextTick();

  //
  // map.fitBounds(L.latLngBounds(markersData.map((item) => item.coords)).pad(0.1));

  //
  markers.forEach((m) => {
    m.openTooltip();
  });

  //
  map.invalidateSize();
  window.setTimeout(map.invalidateSize, 1000);

  // open attribution links in new tab
  const links = containerElement.querySelectorAll("a");
  links.forEach((a) => {
    if (a.href.startsWith("http") && !a.href.includes("#")) {
      // console.log(a);
      a.setAttribute("target", "_blank");
    }
  });

  //
  /*map.on("click", (e) => {
    const { latlng } = e;
    const { lat, lng } = latlng;
    console.log(`${lat}, ${lng}`);
  });*/
}

/*function show(feature) {
  map.flyToBounds(feature.getBounds(), { duration: 0.5 });
  setTimeout(() => {
    feature.openPopup();
  }, 550);
}*/

function onResize() {
  containerWidth.value = containerElement.clientWidth;
}

// lifecycle
onMounted(() => {
  initMap();

  window.addEventListener("resize", onResize);
  onResize();
});

onUnmounted(() => {
  window.removeEventListener("resize", onResize);
});
</script>
